import log from 'loglevel'
import { Button, Card, Typography } from '@mui/material'
import {
  AutocompleteInput,
  Datagrid,
  type Identifier,
  ListBase,
  ListToolbar,
  Loading,
  NotFound,
  Pagination,
  type RaRecord,
  type RowClickFunction,
  TextField,
  TextInput,
  TopToolbar,
  useDataProvider,
  useGetIdentity,
  useListContext,
  usePermissions,
  useTranslate,
  useUnselectAll
} from 'react-admin'
import React, { type ReactElement } from 'react'
import {
  compact,
  concat,
  find,
  flow,
  groupBy,
  includes,
  isEmpty,
  isNil,
  keys,
  map,
  omit,
  reduce,
  replace,
  sortBy,
  uniq,
  uniqBy
} from 'lodash/fp'
import { type ConfigProps } from 'components/Dashboard'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import { MappingsDownloadDialog } from 'components/Download'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { MappingsUploadDialog } from 'components/Upload'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const SpotSelectListContent: React.FC<
  {
    resource: string
    rowClick: RowClickFunction | undefined
    multiple: boolean | undefined
    items: ReactElement[] | undefined
    additionalFilters: ReactElement[] | undefined
    onSelect:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAll:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAllBy:
      | ((selectedIds: Identifier[], data: RaRecord[], allBy: string) => void)
      | undefined
    onSelectAllManagedBy:
      | ((
          selectedIds: Identifier[],
          data: RaRecord[],
          allManagedBy: string
        ) => void)
      | undefined
    setIsOpen: ((value: React.SetStateAction<boolean>) => void) | undefined
    partner: Partial<Partner> | undefined
    allBy: string | null | undefined
    allManagedBy: string | null | undefined
    setUpload: (value: boolean) => void
    setDownload: (value: boolean) => void
    permissions: string[]
  } & ConfigProps
> = ({
  partner,
  resource,
  rowClick,
  multiple,
  items,
  additionalFilters,
  onSelect,
  onSelectAll,
  onSelectAllBy,
  onSelectAllManagedBy,
  config,
  setIsOpen,
  allBy,
  allManagedBy,
  setUpload,
  setDownload,
  permissions
}) => {
  const { SpotList: configSpotList, Spot: configSpot } = config
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const [choices, setChoices] = React.useState<any>(undefined)
  const [allChoices, setAllChoices] = React.useState<any>(undefined)
  const [filteredChoices, setFilteredChoices] = React.useState<any>(undefined)
  const {
    isLoading,
    selectedIds,
    data,
    total,
    filterValues,
    setFilters,
    onSelect: onSelectContext
  } = useListContext()
  const allByValue: string = !isNil(allBy) ? filterValues[allBy] : undefined
  const managedByValue: string = filterValues.managedBy
  const unselectAll = useUnselectAll(resource)
  const handleRowClick: RowClickFunction = async (
    id: Identifier,
    resource: string,
    record: RaRecord
  ) => {
    // eslint-disable-next-line @typescript-eslint/return-await
    if (!isNil(rowClick)) return await rowClick(id, resource, record)
    return false
  }
  const { filters: configFilters } = configSpotList
  // const filters = [
  //   <TextInput source="name" alwaysOn resettable />,
  //   <TextInput source="店番" alwaysOn resettable />,
  //   <SelectInput
  //     source="リージョン"
  //     choices={map((region: string) => ({ id: region, name: region }))(
  //       regions
  //     )}
  //     alwaysOn
  //     resettable
  //   />
  // ]
  const createChoices = (
    source: string,
    groupByItems: string[]
  ): Array<{ id: string; name: string }> => {
    for (const groupByKey of groupByItems) {
      const filterValue = filterValues[groupByKey]
      if (!isNil(filterValue)) {
        const items = groupBy<any>(groupByKey)(allChoices)
        return compact(
          map((item: any) =>
            item[source] === ''
              ? undefined
              : {
                  id: `${item[source] as string}`,
                  name: item[source]
                }
          )(uniqBy<any>(source)(items[replace('$', '')(filterValue)]))
        )
      }
    }
    // return !isNil(filteredChoices) && !isNil(filteredChoices[source])
    //   ? filteredChoices[source]
    //   : map((item: string) => ({
    //       id: `${item}$`,
    //       name: item
    //     }))(choices[source])
    return map((item: string) => ({
      id: `${item}`,
      name: item
    }))(choices[source])
  }
  React.useEffect(() => {
    if (isNil(allChoices)) return
    const items = reduce(
      (
        sum: any,
        item: { source: string; type: string; groupBy?: string[] }
      ) => {
        const { source, type, groupBy: groupByItems } = item
        if (type !== 'select') return sum
        let items: any[]
        if (isNil(groupByItems)) {
          items = map((item: string) => ({
            id: `${item}`,
            name: item
          }))(choices[source])
        } else {
          items = createChoices(source, compact(groupByItems))
        }
        if (
          !isNil(filterValues[source]) &&
          isNil(
            find(
              (item: any) =>
                replace('$', '')(item.id) ===
                replace('$', '')(filterValues[source])
            )(items)
          )
        ) {
          log.debug('error', { source, filterValues, items })
          setFilters(omit([source])(filterValues), [])
        }
        return { ...sum, [source]: items }
      },
      {}
    )(configFilters)
    setFilteredChoices(items)
  }, [allChoices, filterValues])
  const filters =
    isNil(choices) || isNil(filteredChoices)
      ? []
      : compact(
          map(
            (item: {
              source: string
              label?: string
              type: string
              choices: Array<{ label: string; value: string }>
              groupBy?: string[]
            }) => {
              const { source, label, type } = item
              if (isEmpty(source)) return null
              if (type === 'text') {
                return (
                  <TextInput
                    key={source}
                    source={source}
                    label={
                      label ?? configSpotList.fields[source].label ?? source
                    }
                    alwaysOn
                    resettable
                    sx={{ width: 150 }}
                  />
                )
              }
              if (type === 'select') {
                const { groupBy: groupByItems } = item
                const choiceItems = isNil(groupByItems)
                  ? map((item: string) => ({
                      id: `${item}`,
                      name: item
                    }))(choices[source])
                  : filteredChoices[source]
                return (
                  <AutocompleteInput
                    key={source}
                    source={source}
                    label={
                      label ?? configSpotList.fields[source].label ?? source
                    }
                    alwaysOn
                    choices={choiceItems}
                    disabled={isEmpty(choiceItems)}
                    sx={{ width: 150 }}
                  />
                )
              }
              return null
            }
          )(configFilters)
        )
  const handleUpload = (): void => {
    setUpload(true)
  }
  const handleDownload = (): void => {
    setDownload(true)
  }
  const SpotListActions: React.FC = () => {
    return (
      <TopToolbar>
        <Button onClick={handleDownload} startIcon={<DownloadIcon />}>
          <Typography variant="h6">
            {translate('resources.accounts.buttons.download')}
          </Typography>
        </Button>
        <Button onClick={handleUpload} startIcon={<UploadIcon />}>
          <Typography variant="h6">
            {translate('resources.accounts.buttons.upload')}
          </Typography>
        </Button>
      </TopToolbar>
    )
  }
  React.useEffect(() => {
    if (!isNil(onSelect)) onSelect(selectedIds, data)
  }, [selectedIds, data])
  React.useEffect(() => {
    if (!isNil(choices)) return
    if (!isLoading) {
      if (!isNil(setIsOpen)) setIsOpen(true)
      dataProvider
        .getList(resource, {
          pagination: { perPage: total, page: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: { ...filterValues }
        })
        .then(({ data }) => {
          const selectKeys = compact(
            map((item: { type: string; source: string }) =>
              item.type === 'select' ? item.source : undefined
            )(configFilters)
          )
          const choices = {}
          for (const key of selectKeys) {
            choices[key] = flow([
              map((item: RaRecord) => item[key]),
              uniq,
              sortBy((value) => value)
            ])(data)
          }
          setChoices(choices)
          const allChoices = map((item: RaRecord) =>
            reduce((sum: Record<string, string>, key: string) => {
              if (isNil(item[key])) return sum
              return { ...sum, [key]: item[key] }
            }, {})(selectKeys)
          )(data)
          setAllChoices(allChoices)
        })
        .catch(log.error)
    }
  }, [isLoading, setChoices, choices, total])
  if (isLoading) return <Loading />
  const selectAll = (
    onSelectAll: (
      selectedIds: Identifier[],
      data: RaRecord[],
      allBy: string
    ) => void,
    allBy: string
  ): void => {
    dataProvider
      .getList(resource, {
        pagination: { perPage: total, page: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { ...filterValues }
      })
      .then(({ data }) => {
        const ids = map((item: RaRecord) => item.id)(data)
        if (!isEmpty(ids)) {
          onSelectAll(ids, data, allBy)
          onSelectContext(ids)
        }
      })
      .catch(log.error)
  }
  const handleSelectAll = (): void => {
    if (isNil(onSelectAll)) return
    selectAll(onSelectAll, '')
  }
  const handleSelectAllBy = (): void => {
    if (isNil(onSelectAllBy)) return
    selectAll(onSelectAllBy, allByValue)
  }
  const handleSelectAllManagedBy = (): void => {
    if (isNil(onSelectAllManagedBy)) return
    if (isNil(managedByValue)) {
      filterValues.managedBy = allManagedBy
    }
    selectAll(onSelectAllManagedBy, managedByValue ?? allManagedBy)
  }
  const handleClear = (): void => {
    unselectAll()
    if (!isNil(onSelectAll)) onSelectAll([], [])
  }
  const name = configSpot.fields.name.label
  return (
    <>
      {!isNil(onSelectAll) && (
        <Button
          variant="contained"
          onClick={handleSelectAll}
          sx={{ ml: '1em' }}>
          {translate('resources.spotList.buttons.all')}
        </Button>
      )}
      {!isNil(onSelectAllBy) && !isNil(allBy) && (
        <Button
          variant="contained"
          disabled={isNil(allByValue)}
          onClick={handleSelectAllBy}
          sx={{ ml: '1em' }}>{`${allBy as string}が${allByValue ?? '未入力'}の${
          name as string
        }をすべて選択`}</Button>
      )}
      {!isNil(onSelectAllManagedBy) && !isNil(allManagedBy) && (
        <Button
          variant="contained"
          disabled={isNil(allManagedBy) && isNil(managedByValue)}
          onClick={handleSelectAllManagedBy}
          sx={{ ml: '1em' }}>{`担当者が「${managedByValue ?? allManagedBy}」の${
          name as string
        }をすべて選択`}</Button>
      )}
      {(!isNil(onSelect) ||
        (!isNil(onSelectAllBy) && !isNil(allBy)) ||
        (!isNil(onSelectAllManagedBy) && !isNil(allManagedBy))) && (
        <Button variant="outlined" onClick={handleClear} sx={{ ml: '1em' }}>
          {translate('resources.spotList.buttons.clear')}
        </Button>
      )}
      {!isNil(partner) ? (
        <ListToolbar
          filters={
            isNil(additionalFilters)
              ? filters
              : concat(filters)(additionalFilters)
          }
          actions={<SpotListActions />}
        />
      ) : (
        <ListToolbar
          filters={
            isNil(additionalFilters)
              ? filters
              : concat(filters)(additionalFilters)
          }
        />
      )}
      <Card>
        <Datagrid
          id="spotListContent"
          rowClick={handleRowClick}
          bulkActionButtons={
            isNil(multiple) || !(multiple as boolean) ? false : <></>
          }>
          {map((key: string) => {
            const { label, width } = configSpotList.fields[key]
            return (
              <TextField
                key={key}
                source={key}
                label={label ?? key}
                sx={{ display: 'block', width }}
              />
            )
          })(keys(configSpotList.fields))}
          {!isNil(items) && map((item: ReactElement) => item)(items)}
        </Datagrid>
      </Card>
      <Pagination />
    </>
  )
}
export const SpotList: React.FC<
  {
    resource: string
    rowClick?: RowClickFunction
    multiple?: boolean | undefined
    items?: ReactElement[]
    additionalFilters?: ReactElement[]
    onSelect?:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAll?:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAllBy?:
      | ((selectedIds: Identifier[], data: RaRecord[], allBy: string) => void)
      | undefined
    onSelectAllManagedBy?:
      | ((
          selectedIds: Identifier[],
          data: RaRecord[],
          allManagedBy: string
        ) => void)
      | undefined
    setIsOpen?: ((value: React.SetStateAction<boolean>) => void) | undefined
    partner?: Partial<Partner> | undefined
    allBy?: string | null | undefined
    allManagedBy?: string | null | undefined
  } & ConfigProps
> = ({
  partner,
  resource,
  rowClick,
  multiple,
  items,
  additionalFilters,
  onSelect,
  onSelectAll,
  onSelectAllBy,
  onSelectAllManagedBy,
  config,
  setIsOpen,
  allBy,
  allManagedBy
}) => {
  const { isLoading: isGetIdentityLoading, data: identityData } =
    useGetIdentity()
  const { permissions, isLoading: isPermissionsLoading } = usePermissions()
  const { SpotList: configSpotList } = config
  const [upload, setUpload] = React.useState(false)
  const [download, setDownload] = React.useState(false)
  if (isGetIdentityLoading || isPermissionsLoading) return <Loading />
  if (isNil(identityData)) return <NotFound />
  const { name } = identityData['cognitoUser'].signInUserSession.idToken.payload
  const filter = includes('owner')(permissions)
    ? { ownedBy: name }
    : (configSpotList.restrictManager as boolean) &&
      includes('manager')(permissions) &&
      !includes('admin')(permissions)
    ? { managedBy: name }
    : {}
  const { sortBy } = configSpotList
  const sort = { field: isNil(sortBy) ? 'name' : sortBy, order: 'ASC' }
  return (
    <>
      <ListBase
        resource={resource}
        filter={filter}
        disableSyncWithLocation
        debounce={1000}
        sort={sort}>
        <SpotSelectListContent
          partner={partner}
          resource={resource}
          rowClick={rowClick}
          multiple={multiple}
          items={items}
          additionalFilters={additionalFilters}
          onSelect={onSelect}
          onSelectAll={onSelectAll}
          onSelectAllBy={onSelectAllBy}
          onSelectAllManagedBy={onSelectAllManagedBy}
          config={config}
          setIsOpen={setIsOpen}
          allBy={allBy}
          allManagedBy={allManagedBy}
          setUpload={setUpload}
          setDownload={setDownload}
          permissions={permissions}
        />
      </ListBase>
      {!isNil(partner) && (
        <MappingsUploadDialog
          open={upload}
          setOpen={setUpload}
          partner={partner}
        />
      )}
      {!isNil(partner) && (
        <MappingsDownloadDialog
          open={download}
          setOpen={setDownload}
          partner={partner}
          config={config}
        />
      )}
    </>
  )
}
